import { useState, useCallback } from "react";
import { ArrowDown, ArrowUp } from "../../Utils/Keys";
import { useHotkey } from "../useHotkey/useHotkey";
/**
 * Hook to allow arrow key focus navigation, the 'focus' prop on the component should be bound to 'focusIndex === index'
 * See ListBox and "Implementation of component with arrow key focus and multiple elements" tests for implementation example.
 * @param size The number of elements in the control to have focus added to it.
 * @param ref A ref where you want the ArrowKeyFocus to apply to/within.
 * @param autoFocus A boolean for if you want the first element within your ref to have focus.
 * @returns [focusIndex: number, onFocus: (number) => void] The current focused index, a function to bind to onFocus to sync the current index with other sources of focus (clicks, tabs etc).
 */
export function useArrowKeyFocus(size, ref, autoFocus = false) {
    const [focusIndex, setfocusIndex] = useState(autoFocus ? 0 : -1);
    useHotkey(useCallback(() => setfocusIndex(focusIndex >= size - 1 ? 0 : focusIndex + 1), [focusIndex, size]), ref, ArrowDown);
    useHotkey(useCallback(() => setfocusIndex(focusIndex <= 0 ? size - 1 : focusIndex - 1), [focusIndex, size]), ref, ArrowUp);
    const onItemFocus = useCallback(
    //setting focus via arrows is handled already, but this should be attached to the onFocus event to keep index in sync with other sources of focus
    (index) => {
        if (index !== focusIndex) {
            setfocusIndex(index);
        }
    }, [focusIndex, setfocusIndex]);
    const onContainerBlur = (event) => {
        //we only want to know when focus has left the whole element, not the children (as that would fire whenever the user pressed an arrow key to change focus on a child and that wouldn't be very useful)
        // This was written prior to adding @typescript-eslint/consistent-type-assertions, please refactor when possible.
        // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
        const newFocusTarget = event.relatedTarget;
        const newFocusIsOutsideOfContainer = !ref.current?.contains(newFocusTarget);
        if (newFocusIsOutsideOfContainer) {
            setfocusIndex(-1);
        }
    };
    return { focusIndex: focusIndex, onItemFocus: onItemFocus, onContainerBlur: onContainerBlur };
}
